import React, { useEffect, useState } from "react";
import { ChevronRight, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useBooking } from "@/context/BookingContext";
import { StepTitle, ErrorState, Skeleton } from "@/components/BookingShell";
import { formatPrice } from "@/lib/format";
import { BookingSuggestions } from "@/components/BookingSuggestions";

// Sprint 36.16 — final Pout-branded category imagery, stored locally in
// /app/frontend/public/category-images/ and served from /category-images/*.
// These are the authoritative artwork files for their categories and MUST
// override any Amelia `pictureFullPath` (which may be stale/random). See
// the resolver chain in the card render below.
//
// DO NOT expand this list to include external URLs. Only local files under
// /category-images/ belong here. Every filename must correspond to a real
// file in the /public directory.
const CATEGORY_IMAGES = [
    // Retained as final-fallback only — used ONLY when an entirely new
    // category appears in Amelia that isn't in CATEGORY_IMAGE_MAP yet.
    "/category-images/category-consultations.webp",
];

const CATEGORY_IMAGE_MAP = {
    // Face / injectable treatments
    consultations:                       "/category-images/category-consultations.webp",
    antiwrinkletreatments:               "/category-images/category-anti-wrinkle.webp",
    skinboosterspolynucleotides:         "/category-images/category-skin-boosters-polynucleotides.webp",
    dermalfillers:                       "/category-images/category-dermal-fillers.webp",
    fatdissolving:                       "/category-images/category-fat-dissolving.webp",
    facialsdermaplanningmicroneedling:   "/category-images/category-facials-dermaplaning.webp",
    microneedlingmesotherapy:            "/category-images/category-microneedling-mesotherapy.webp",
    vitamininjections:                   "/category-images/category-vitamin-injections.webp",
    hairfiller:                          "/category-images/category-hair-filler.webp",
    // Beauty room treatments
    nails:                               "/category-images/category-nails.webp",
    earpiercing:                         "/category-images/category-ear-piercing.webp",
    lashtint:                            "/category-images/category-lash-tint.webp",
    eyebrows:                            "/category-images/category-eyebrows.webp",
    waxing:                              "/category-images/category-waxing.webp",
};

function _slugCategoryName(name) {
    return String(name || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function _titleClassForLength(name) {
    // Sprint 36.17 — intelligent title sizing.
    // Bucketed by character count. Values tuned so every category name
    // fully renders (up to 3 lines) inside the desktop card (~269 px wide,
    // ~200 px content after padding + arrow reserve).
    const len = (name || "").length;
    if (len <= 12)  return "text-3xl md:text-4xl";     // Nails, Waxing, Eyebrows, Hair filler…
    if (len <= 18)  return "text-2xl md:text-3xl";     // Consultations, Dermal Fillers, Fat Dissolving, Lash tint…
    if (len <= 26)  return "text-xl md:text-2xl";      // Anti Wrinkle Treatments, Vitamin injections…
    if (len <= 30)  return "text-lg md:text-xl";       // Skin boosters/Polynucleotides, Micro-Needling / Mesotherapy
    return "text-base md:text-lg";                     // Facials/dermaplanning/microneedling (35 chars)
}

export function CategoryStep() {
    const { setCategory, goTo } = useBooking();
    const [categories, setCategories] = useState(null);
    const [error, setError] = useState(null);

    const load = () => {
        setError(null);
        setCategories(null);
        api.listCategories().then(setCategories).catch(setError);
    };
    useEffect(load, []);

    const onPick = (cat) => {
        setCategory(cat);
        goTo("service");
    };

    return (
        <div>
            <StepTitle
                eyebrow="Begin"
                title={<>Choose your <em className="italic gold-text not-italic font-serif">treatment</em> category</>}
                subtitle="Curated, results-driven aesthetic treatments delivered by Angela in a discreet, private setting."
                testId="category-step-title"
            />

            {error && <ErrorState error={error} onRetry={load} />}
            <BookingSuggestions showBasketSummary />

            {/*
              Sprint 36.17 — Grid "break-out" wrapper.
              The parent BookingShell container is capped at max-w-xl (576px)
              which suits copy but leaves the category grid feeling lost on
              wide desktops. On lg+ we escape that constraint by centring the
              grid on the viewport and giving it its own generous cap
              (max-w-4xl = 896px), while mobile/tablet remain unchanged so
              they keep their perfectly-sized single/dual columns.
            */}
            <div className="mt-2 lg:relative lg:left-1/2 lg:-translate-x-1/2 lg:w-screen lg:max-w-4xl lg:px-6">
                {!categories && !error && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="category-skeleton">
                        {[1, 2, 3, 4, 5, 6].map((k) => (
                            <Skeleton key={k} className="aspect-[4/3] rounded-2xl" />
                        ))}
                    </div>
                )}

                {categories && categories.length === 0 && (
                    <div className="rounded-2xl glass p-8 text-center" data-testid="category-empty-state">
                        <Sparkles className="h-6 w-6 mx-auto text-gold mb-3" strokeWidth={1.25} />
                        <div className="font-serif text-2xl">No treatments available yet</div>
                        <p className="mt-2 text-sm text-white/55">Please check back shortly.</p>
                    </div>
                )}

                {categories && categories.length > 0 && (
                    <ul
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 auto-rows-fr items-stretch justify-items-stretch"
                        data-testid="category-list"
                    >
                        {categories.map((c, i) => {
                            // Sprint 36.16 — CATEGORY_IMAGE_MAP wins over Amelia uploads. Legacy fallback last.
                            const slug = _slugCategoryName(c.name);
                            const img =
                                CATEGORY_IMAGE_MAP[slug]
                                || c.pictureFullPath
                                || c.pictureThumbPath
                                || CATEGORY_IMAGES[i % CATEGORY_IMAGES.length];
                            const fromPrice = c.services.length
                                ? Math.min(...c.services.map((s) => Number(s.price) || 0).filter((p) => p > 0))
                                : null;
                            const titleSizeClass = _titleClassForLength(c.name);
                            return (
                                <li key={c.id} className="h-full">
                                    <button
                                        onClick={() => onPick(c)}
                                        data-testid={`category-card-${c.id}`}
                                        className="cat-card group relative w-full h-full text-left rounded-2xl overflow-hidden
                                                   aspect-[4/3] focus:outline-none focus-visible:outline-none"
                                    >
                                        {/* Image layer — independent scale on hover, clipped by parent overflow-hidden. */}
                                        <div
                                            className="cat-card__img absolute inset-0 bg-cover bg-center"
                                            style={{ backgroundImage: `url(${img})` }}
                                        />
                                        {/* Base bottom-weighted gradient for text legibility over artwork. */}
                                        <div className="cat-card__scrim absolute inset-0 pointer-events-none" />

                                        {/* Sprint 36.17 — two-region content (title middle, price+arrow bottom).
                                            Treatment-count row removed per product decision. Longer titles
                                            grow upward inside the middle slot; price/arrow row stays pinned. */}
                                        <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-6 min-w-0">
                                            <div className="flex-1 flex items-end pr-6 md:pr-7">
                                                <h3
                                                    className={`font-serif leading-[1.1] text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.55)] ${titleSizeClass}`}
                                                    style={{
                                                        overflowWrap: "normal",
                                                        wordBreak: "normal",
                                                        display: "-webkit-box",
                                                        WebkitBoxOrient: "vertical",
                                                        WebkitLineClamp: 3,
                                                        overflow: "hidden",
                                                    }}
                                                >
                                                    {/* Sprint 36.17 — inject zero-width spaces after
                                                        slashes so the browser can wrap at natural
                                                        separators without ever breaking mid-word.
                                                        Invisible on screen and to screen readers. */}
                                                    {c.name.replace(/([\/-])/g, "$1\u200B")}
                                                </h3>
                                            </div>
                                            <div className="mt-3 flex items-center justify-between gap-2">
                                                {fromPrice ? (
                                                    <span className="text-xs md:text-sm text-white/85 whitespace-nowrap tracking-wide">
                                                        from <span className="text-white font-medium">{formatPrice(fromPrice)}</span>
                                                    </span>
                                                ) : <span />}
                                                <ChevronRight className="cat-card__arrow h-5 w-5 text-gold shrink-0" strokeWidth={1.75} />
                                            </div>
                                        </div>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            {/* Sprint 36.17 — scoped card styling (gold frame, hover choreography,
                reduced-motion + hover-only guards). Kept inline to keep the whole
                polish pass contained in a single file. */}
            <style>{`
                .cat-card {
                    border: 2px solid rgba(196, 160, 91, 0.55);
                    box-shadow: 0 0 0 1px rgba(196,160,91,0.08), 0 4px 16px rgba(4,10,32,0.35);
                    transition: transform 300ms ease, box-shadow 300ms ease, border-color 300ms ease;
                    will-change: transform;
                }
                .cat-card__img {
                    transition: transform 500ms ease;
                }
                .cat-card__scrim {
                    background: linear-gradient(
                        to top,
                        rgba(4, 10, 32, 0.85) 0%,
                        rgba(4, 10, 32, 0.55) 32%,
                        rgba(4, 10, 32, 0.15) 62%,
                        rgba(4, 10, 32, 0.0) 100%
                    );
                    transition: opacity 300ms ease;
                }
                .cat-card__arrow {
                    transition: transform 300ms ease, filter 300ms ease;
                }
                .cat-card:focus-visible {
                    border-color: rgba(196, 160, 91, 1);
                    box-shadow: 0 0 0 3px rgba(196,160,91,0.35), 0 8px 28px rgba(4,10,32,0.5);
                }
                @media (hover: hover) and (pointer: fine) {
                    .cat-card:hover {
                        transform: translateY(-3px) scale(1.03);
                        border-color: rgba(196, 160, 91, 0.95);
                        box-shadow:
                            0 0 0 1px rgba(196,160,91,0.35),
                            0 0 32px rgba(196,160,91,0.28),
                            0 18px 40px rgba(4,10,32,0.55);
                    }
                    .cat-card:hover .cat-card__img {
                        transform: scale(1.04);
                    }
                    .cat-card:hover .cat-card__scrim {
                        background: linear-gradient(
                            to top,
                            rgba(4, 10, 32, 0.92) 0%,
                            rgba(4, 10, 32, 0.60) 32%,
                            rgba(4, 10, 32, 0.18) 62%,
                            rgba(4, 10, 32, 0.0) 100%
                        );
                    }
                    .cat-card:hover .cat-card__arrow {
                        transform: translateX(4px);
                        filter: drop-shadow(0 0 6px rgba(196,160,91,0.6));
                    }
                }
                @media (prefers-reduced-motion: reduce) {
                    .cat-card, .cat-card__img, .cat-card__scrim, .cat-card__arrow {
                        transition: none !important;
                    }
                    .cat-card:hover {
                        transform: none !important;
                    }
                    .cat-card:hover .cat-card__img,
                    .cat-card:hover .cat-card__arrow {
                        transform: none !important;
                    }
                }
            `}</style>
        </div>
    );
}
